import './EditUserForm.scoped.scss'
import Button from 'Shared/components/Button'
import { CircularProgress } from '@material-ui/core'
import restClient from 'Shared/hooks/restClient'
import { Formik, FieldArray, Form } from 'formik'
import * as Yup from 'yup'
import FormikField from 'Shared/components/FormikField/FormikField'
import FormikSelect from 'Shared/components/FormikSelect/FormikSelect'
import UserRole from './UserRole'

/**
 * user.roleAssignments: Array<{ organisationId: number|null, roleId: number }>
 */

const statusOptions = [
  { value: 'approved', label: 'Approved' },
  { value: 'awaiting_approval', label: 'Awaiting approval' },
  { value: 'rejected', label: 'Rejected' },
]

function willRequirePhoneNumber(roles, { phoneNumber, roleAssignments }) {
  const volunteerRole = roles.find((r) => r.name === 'Volunteer')
  const hasNonVolunteer = (roleAssignments || []).some(
    (ra) => ra?.roleId && ra.roleId !== volunteerRole?.id
  )
  return (phoneNumber || '').trim() === '' && hasNonVolunteer
}

function dedupeAssignments(assignments) {
  const seen = new Set()
  return (assignments || []).filter((a) => {
    if (!a) return false
    const key = `${a.organisationId ?? 'null'}:${a.roleId ?? 'null'}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const RoleAssignmentSchema = Yup.object({
  organisationId: Yup.number()
    .nullable()
    .required('Select an organisation'),
  roleId: Yup.number()
    .nullable()
    .required('Select a role'),
})

const EditUserSchema = Yup.object({
  firstName: Yup.string().trim().required('First name is required'),
  lastName: Yup.string().trim().required('Last name is required'),
  roleAssignments: Yup.array().of(RoleAssignmentSchema)
})

const EditUserForm = ({ user: userProp, roles, inviteAcceptedAt }) => {
  const [showSuccess, setShowSuccess] = React.useState(false)
  const isGlobalAdmin = useSel((s) => s.currentUser.globalAdmin)
  const roleIdsCanInvite = useSel((s) => s.currentUser.roleIdsCanInvite)
  const isEditingSelf = useSel((s) => s.currentUser.id === userProp?.id)

  const initialUser = React.useMemo(
    () => ({
      ...(userProp || {}),
      roleAssignments: userProp?.roleAssignments ?? [],
    }),
    [userProp]
  )

  const handleSubmit = (values, formik) => {
    const user = { ...values }

    // Only send deduped roleAssignments; no legacy fields
    user.roleAssignments = dedupeAssignments(
      (user.roleAssignments || []).filter((ra) => ra && ra.roleId)
    )

    restClient
      .put(`/api/users/${user.id}`, {
        user: {
          firstName: values.firstName,
          lastName: values.lastName,
          phoneNumber: values.phoneNumber,
          status: values.status,
          roleAssignments: user.roleAssignments
        }
      })
      .then((response) => {
        setShowSuccess(true)

        // trust the values back
        const next = {
          ...(response.data || {}),
          roleAssignments: response.data.roleAssignments,
        }

        formik.setValues(next)
        formik.setSubmitting(false)
      })
      .catch((e) => {
        setShowSuccess(false)
        formik.setSubmitting(false)
        formik.setErrors(e.response.data)
      })
  }

  return (
    <Formik initialValues={initialUser} onSubmit={handleSubmit} validationSchema={EditUserSchema} enableReinitialize>
      {(formik) => {
        const { values, isSubmitting, errors, setFieldValue } = formik
        const requirePhone = willRequirePhoneNumber(roles, values)

        return (
          <Form>
            <div className="EditUserForm details">
              <div className="detail">
                <div className="left"><p>Name</p></div>
                <div className="right field" id="name-field">
                  <label htmlFor="first-name">First</label>
                  <FormikField name="firstName" type="text" id="first-name" />
                  <label htmlFor="last-name">Last</label>
                  <FormikField name="lastName" type="text" id="last-name" />
                </div>
              </div>

              <div className="detail">
                <div className="left"><p>Status</p></div>
                <div className="right field">
                  <FormikSelect
                    name="status"
                    classNamePrefix="status-select"
                    options={statusOptions}
                  />
                </div>
              </div>

              <div className="detail">
                <div className="left"><p>Email</p></div>
                <div className="right">{values.email}</div>
              </div>

              <div className="detail">
                <div className="left"><p>Phone number</p></div>
                <div className="right field">
                  <FormikField name="phoneNumber" type="text" />
                  {requirePhone && (
                    <p className="margin-top half error red bold">
                      The roles this user has will mean they need to add a phone number before they can access their account.
                    </p>
                  )}
                </div>
              </div>

              <div className="detail">
                <div className="left"><p>Roles</p></div>
                <div className="right field role-assignments">
                  {values.roleAssignments && values.roleAssignments.length == 0 &&
                    <p className='margin-bottom'>This user has no roles, so they won't be able to log in to Movement currently.</p>
                  }
                  <FieldArray name="roleAssignments">
                    {(arrayHelpers) => (
                      <>
                        {(values.roleAssignments || []).map((_, idx) => (
                          <UserRole
                            key={idx}
                            index={idx}
                            values={values}
                            setFieldValue={setFieldValue}
                            roles={roles}
                            roleIdsCanInvite={roleIdsCanInvite}
                            isEditingSelf={isEditingSelf}
                            onRemove={() => arrayHelpers.remove(idx)}
                          />
                        ))}
                        <div className="add-row">
                          <Button
                            type="button"
                            secondary
                            small
                            onClick={() => {
                              const newValues = {
                                ...values,
                                roleAssignments: [...values.roleAssignments, { organisationId: null, roleId: null }]
                              }
                              formik.resetForm({ values: newValues }) // reset validations
                            }}
                          >
                            Add role
                          </Button>
                        </div>
                      </>
                    )}
                  </FieldArray>
                </div>
              </div>

              {inviteAcceptedAt && (
                <div className="detail">
                  <div className="left"><p>Accepted invite at</p></div>
                  <div className="right field">{inviteAcceptedAt}</div>
                </div>
              )}
            </div>

            {errors && (
              <div className="error-message margin-top">
                {[].concat(errors.base).filter(Boolean).join(', ')}
              </div>
            )}

            <div className="field save-changes margin-top">
              <Button type="submit" primary disabled={isSubmitting}>
                Save changes
              </Button>
              {isSubmitting && <CircularProgress />}
              {showSuccess && <p className='bold green'>User updated.</p>}
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

export default EditUserForm
